import { useEffect, useRef } from 'react';

/**
 * One filter axis, behind one button.
 *
 * ── The trade this makes ───────────────────────────────────────────────────
 *
 * A chip shows you its options without being asked; a dropdown makes you open
 * it first. Kyu asked for the dropdown — "It's still a bit funky doing these
 * filters. Maybe change them to Dropdown" — because the chips were costing the
 * whole screen to show options nobody was reading. So this trades one click
 * for silence, on every axis including Status.
 *
 * Status was the arguable one. It stays a menu anyway, because Status was the
 * row he circled: seven chips permanently open under the search box with
 * nothing explaining them. A row of six identical buttons is legible in a way
 * that "a row plus a wall" is not.
 *
 * ── Why it is not the `Filters` disclosure again ───────────────────────────
 *
 * The old disclosure expanded a panel IN PLACE, and the comment on it argued
 * that "a popover over a dense table hides the thing you are filtering while
 * you filter it". That argument was right about a panel holding 35 chips and
 * wrong about a menu holding seven: the panel was 240px of page, a menu is
 * 224px wide and closes the moment you look away. The in-place panel is what
 * pushed the leads table down the screen every time anybody touched a filter.
 *
 * ── What it owes the person using it ───────────────────────────────────────
 *
 * A control that hides its own state is a list that lies about what it is
 * showing. Two things stop that here and both are tested:
 *   - `count` puts a badge on the button whenever this axis is narrowing
 *   - the active-filter row below the bar names every live filter regardless
 *
 * Closing: Escape, a click outside, or the button again. Nothing closes on
 * choosing a value — these are multi-select in the common case, and a menu
 * that shuts after one tick makes ticking three of them a three-click,
 * three-reopen chore.
 */
export function FilterMenu({
  label,
  count,
  open,
  onOpenChange,
  children,
}: {
  label: string;
  /** How many conditions this axis is holding. 0 draws no badge. */
  count: number;
  open: boolean;
  /** Called with true to open this menu, false to close it. */
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const wrap = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (event: MouseEvent) => {
      // `mousedown` rather than `click`: a click that starts inside the menu
      // and ends outside it — a drag while selecting a label — would close the
      // menu under the cursor on `click`, which reads as the app fighting you.
      if (wrap.current !== null && !wrap.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <span className="lead-filter-menu" ref={wrap}>
      <button
        type="button"
        // The SAME class the Rounds button uses. A new button style for a new
        // control is how a bar ends up with four kinds of button in one row.
        className={`lead-filter-more${count > 0 ? ' lead-filter-more--active' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          onOpenChange(!open);
        }}
      >
        {label}
        {count === 0 ? null : <span className="lead-filter-more-count">{count}</span>}
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {!open ? null : (
        <div className="lead-filter-menu-panel" role="group" aria-label={label}>
          {children}
        </div>
      )}
    </span>
  );
}

/**
 * One line in a menu.
 *
 * `kind` changes the tick only: ✓ for an axis you can hold several of, ● for
 * one that takes a single answer. Two glyphs rather than two components,
 * because the difference is genuinely that small and the alternative was a
 * checkbox in one menu and a radio in the next, which makes the menus look
 * like they came from different applications.
 */
export function FilterMenuItem({
  label,
  on,
  kind,
  title,
  onClick,
}: {
  label: string;
  on: boolean;
  kind: 'many' | 'one';
  /** Hover explanation. The follow-up queues need one; the names are terse. */
  title?: string | undefined;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`lead-filter-menu-item${on ? ' lead-filter-menu-item--on' : ''}`}
      aria-pressed={on}
      title={title}
      onClick={onClick}
    >
      {/* Fixed width whether or not it holds a glyph, so the labels start on
          one x. A list that shifts sideways as you tick it down is the thing
          that makes a menu feel cheap. */}
      <span className="lead-filter-menu-tick" aria-hidden="true">
        {on ? (kind === 'many' ? '✓' : '●') : ''}
      </span>
      {label}
    </button>
  );
}
